/**
 * @fileoverview Vertical borders component for consistent app-wide layout structure
 * @module components/layout/VerticalBorders
 */

import type { ReactElement } from 'react';

/**
 * Props for the VerticalBorders component.
 */
interface VerticalBordersProps {
  /** Custom color for the borders (defaults to #282828) */
  color?: string;
  /** Custom width for the navbar alignment (defaults to 1300px) */
  navbarWidth?: number;
}

/**
 * Vertical borders component that creates consistent layout structure.
 * 
 * Renders two vertical lines that align with the navbar's left and right edges,
 * providing visual structure and content framing throughout the entire application.
 * 
 * Features:
 * - Aligns with navbar boundaries (1300px width by default)
 * - Fixed positioning for consistent placement on all routes
 * - Customizable color and navbar width
 * - Extends from top to bottom of viewport
 * - Positioned behind content (z-index 40)
 * 
 * @component
 * @example
 * ```tsx
 * // Default usage (matches navbar)
 * <VerticalBorders />
 * 
 * // Custom color
 * <VerticalBorders color="#444444" />
 * 
 * // Custom navbar width alignment
 * <VerticalBorders navbarWidth={1200} />
 * ```
 */
export function VerticalBorders({
  color = '#282828',
  navbarWidth = 1300,
}: VerticalBordersProps = {}): ReactElement {
  // Calculate positioning based on navbar width
  const halfWidth = navbarWidth / 2;
  const positioning = `calc(50% - ${halfWidth}px)`;

  return (
    <>
      {/* Left vertical border */}
      <div
        className="fixed top-0 bottom-0 w-[1px] z-40 pointer-events-none"
        style={{
          left: positioning,
          backgroundColor: color,
        }}
        aria-hidden="true"
      />
      
      {/* Right vertical border */}
      <div
        className="fixed top-0 bottom-0 w-[1px] z-40 pointer-events-none"
        style={{
          right: positioning,
          backgroundColor: color,
        }}
        aria-hidden="true"
      />
    </>
  );
}